import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import {
  THEMES,
  DEFAULT_THEME_ID,
  ALL_THEME_CLASSNAMES,
  THEME_ELIGIBLE_ROLES,
  pathIsForcedDark,
  getThemeById,
  listAvailableThemes,
} from '../lib/themes';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ThemeContext = createContext({
  theme: DEFAULT_THEME_ID,
  effectiveTheme: DEFAULT_THEME_ID,
  canToggle: false,
  setTheme: () => {},
  themes: [],
});

export const useTheme = () => useContext(ThemeContext);

export const ThemeProvider = ({ children }) => {
  const { user } = useAuth();
  const location = useLocation();
  const canToggle = !!user && THEME_ELIGIBLE_ROLES.has(user.role);

  // Optimistic override — set immediately when the user toggles, cleared on user change.
  // We avoid syncing state inside a useEffect (which triggers React's strict-mode lint
  // and an extra render). Instead the displayed theme is a memo derived from override + user.
  const [override, setOverride] = useState(null);

  // Reset override when the underlying user changes (login/logout/refresh).
  useEffect(() => {
    // eslint-disable-next-line
    setOverride(null);
  }, [user?.id]);

  // The user's saved preference (the theme they want).
  const theme = useMemo(() => {
    if (override && THEMES[override]) return override;
    const pref = canToggle ? user?.theme_preference : DEFAULT_THEME_ID;
    return THEMES[pref] ? pref : DEFAULT_THEME_ID;
  }, [override, canToggle, user?.theme_preference]);

  // Effective theme: forced-dark on public/customer/VIP/login surfaces, otherwise saved pref.
  const forceDark = !canToggle || pathIsForcedDark(location.pathname || '');
  const effectiveTheme = forceDark ? DEFAULT_THEME_ID : theme;

  // Apply class to <html> + <body>. Strip all other theme classes first.
  useEffect(() => {
    const root = document.documentElement;
    const body = document.body;
    const activeClass = getThemeById(effectiveTheme).className;
    ALL_THEME_CLASSNAMES.forEach((cls) => {
      root.classList.toggle(cls, cls === activeClass);
      body.classList.toggle(cls, cls === activeClass);
    });
    root.setAttribute('data-theme', effectiveTheme);
  }, [effectiveTheme]);

  const setTheme = useCallback(async (nextId) => {
    if (!canToggle) return;
    if (!THEMES[nextId]) return;
    setOverride(nextId);
    try {
      await axios.put(`${API}/user/theme`, { theme: nextId }, { withCredentials: true });
    } catch { /* offline; theme persists in-session via override */ }
  }, [canToggle]);

  const value = useMemo(() => ({
    theme,
    effectiveTheme,
    canToggle,
    setTheme,
    themes: listAvailableThemes(),
    forceDark,
  }), [theme, effectiveTheme, canToggle, setTheme, forceDark]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};
